import { PomodoroTimer } from './../../../../common/models/pomodoro-timer.model';

import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ButtonModule, DropdownModule, InputtextModule, ModalRef, ModalTemplateModule } from '@atsystems/ui';
import { PomodoroTimerService } from 'src/app/common/services/pomodoro-timer.service';
import { CheckboxWideComponent } from 'src/app/ui/modules/checkbox-wide/checkbox-wide.component';

@Component({
  selector: 'ats-pomodoro-settings-modal',
  imports: [ModalTemplateModule, ButtonModule, InputtextModule, DropdownModule, ReactiveFormsModule, CheckboxWideComponent],
  templateUrl: './pomodoro-settings-modal.component.html',
  styleUrl: './pomodoro-settings-modal.component.sass',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PomodoroSettingsModalComponent {
  private pomodoroTimerService = inject(PomodoroTimerService);
  private destroyRef = inject(DestroyRef);
  private modalRef = inject(ModalRef);
  private fb = inject(FormBuilder);

  public channel = new BroadcastChannel('pomodoro-timer');

  public data = this.pomodoroTimerService.data
  public loading = signal(false);

  public form = this.fb.group({
    workDuration: [this.data()?.workDuration ?? 25, [Validators.required, Validators.min(1), Validators.max(60)]],
    shortBreakDuration: [this.data()?.shortBreakDuration ?? 5, [Validators.required, Validators.min(1), Validators.max(60)]],
    longBreakDuration: [this.data()?.longBreakDuration ?? 15, [Validators.required, Validators.min(1), Validators.max(60)]],
    iterationCount: [this.data()?.iteration.total ?? 4, [Validators.required, Validators.min(4), this.evenNumberValidator]],
    needConfirm: [this.data()?.needConfirm ?? true],
    needSound: [this.data()?.needSound ?? true],
  });

  private evenNumberValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value && value % 2 !== 0) {
      return { evenNumber: true };
    }
    return null;
  }

  constructor() {}

  public close() {
    this.modalRef.close();
  }

  public save() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.getRawValue();
    const payload: PomodoroTimer = {
      workDuration: Number(formValue.workDuration) ?? 0,
      shortBreakDuration: Number(formValue.shortBreakDuration) ?? 0,
      longBreakDuration: Number(formValue.longBreakDuration) ?? 0,
      iteration: {
        current: this.pomodoroTimerService.currentIteration() ?? 1,
        total: Number(formValue.iterationCount) ?? 4,
      },
      lastStart: this.pomodoroTimerService.startTime(),
      openByDefault: this.pomodoroTimerService.isActive(),
      needConfirm: formValue.needConfirm ?? true,
      needSound: formValue.needSound ?? true,
    };

    this.loading.set(true);

    this.pomodoroTimerService.setTimerConfig(payload).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.channel.postMessage({reload: true});
      this.loading.set(false);
      this.close();
    });
  }
}
